import { pipe } from 'fp-ts/function';
import { array, option } from 'fp-ts';
import { DropdownItem, IconName } from '@optimus-web/core';
import { TextKey } from '@mews-commander/enums';
import { FormSelectField, FormSelectFieldProps, LocalizedText, useFieldDependency } from '@mews/framework';
import { TooltippedIcon } from '@mews-commander/ui';
import { FormValues } from '../../types';
import { WithEnterpriseIds } from '../../../../../../types';
import { LockableField } from '../../../LockableField';
import { BUSINESS_SEGMENT_KEY_FIELD_NAME, RATE_GROUP_KEY_FIELD_NAME } from '../fieldNames';
import { useRateGroups } from '../../../../../../queries';
import { WithFieldsConfig } from '../types';
import { getFieldLockedConfig } from '../helpers';
import { useRateFormContext } from '../../hooks/RateFormProvider';
import { useCommonBusinessSegmentKeys } from './useCommonBusinessSegmentKeys';

const renderItems = (keys: string[]) => keys.map((key) => <DropdownItem key={key} value={key} label={key} />);

type SelectFieldProps = Omit<FormSelectFieldProps, 'children' | 'name' | 'label' | 'required' | 'size' | 'helper'> & {
    disabledReasonTextKey?: Mews.TextKey;
    keys: string[];
    addEmptyOption?: boolean;
};

const SelectField = ({ disabledReasonTextKey, keys, addEmptyOption = false, ...other }: SelectFieldProps) => (
    <FormSelectField
        {...other}
        name={BUSINESS_SEGMENT_KEY_FIELD_NAME}
        label={<LocalizedText textKey={TextKey.BusinessSegmentKey} />}
        helper={disabledReasonTextKey ? <LocalizedText textKey={disabledReasonTextKey} /> : null}
        captionIconWithTooltip={
            <TooltippedIcon size="small" value={IconName.Help} tooltip={TextKey.BusinessSegmentKeyHelperText} />
        }
    >
        {addEmptyOption ? <DropdownItem value="" label="-" /> : null}
        {renderItems(keys)}
    </FormSelectField>
);

type BusinessSegmentKeyFieldProps = WithEnterpriseIds &
    WithFieldsConfig & {
        isCreateForm: boolean;
        selectedRateIds?: string[];
        onUnlock?: () => void;
    };

export const BusinessSegmentKeyField = ({
    enterpriseIds,
    fieldsConfig,
    isCreateForm,
    selectedRateIds,
    onUnlock,
}: BusinessSegmentKeyFieldProps) => {
    const isLocked = getFieldLockedConfig(BUSINESS_SEGMENT_KEY_FIELD_NAME, fieldsConfig);
    const { rateResults } = useRateFormContext();

    const selectedRateServiceIds =
        selectedRateIds && rateResults?.rates ? rateResults?.rates.map((rate) => rate.serviceId) : [];

    const { dependencyValue: rateGroupExternalIdentifier } = useFieldDependency<
        FormValues,
        typeof RATE_GROUP_KEY_FIELD_NAME
    >({
        name: BUSINESS_SEGMENT_KEY_FIELD_NAME,
        dependency: RATE_GROUP_KEY_FIELD_NAME,
    });

    const { data: rateGroupResults } = useRateGroups(enterpriseIds, { enabled: selectedRateIds == null });
    const rateGroups = rateGroupResults?.rateGroups || [];

    const serviceIds =
        (selectedRateIds && selectedRateServiceIds) ||
        pipe(
            rateGroups,
            array.filterMap((rateGroup) =>
                rateGroup.externalIdentifier === rateGroupExternalIdentifier
                    ? option.some(rateGroup.serviceId)
                    : option.none
            )
        );

    const { data: commonBusinessSegmentKeys, isFetching } = useCommonBusinessSegmentKeys({
        enterpriseIds,
        serviceIds,
    });

    if (!rateGroupExternalIdentifier) {
        return (
            <SelectField
                disabledReasonTextKey={TextKey.SelectRateGroupFirst}
                loader={isFetching}
                keys={commonBusinessSegmentKeys}
                disabled
            />
        );
    }

    const isEmpty = array.isEmpty(commonBusinessSegmentKeys);
    const disabledReasonTextKey =
        enterpriseIds.length > 1 ? TextKey.NoCommonBusinessSegmentPlural : TextKey.NoCommonBusinessSegment;

    return !isEmpty ? (
        <LockableField
            fieldName={BUSINESS_SEGMENT_KEY_FIELD_NAME}
            locked={isLocked}
            lockedReasonText={TextKey.RatesValuesAreDifferent}
            tooltipTextKey={TextKey.UnlockableRatesField}
            onUnlock={onUnlock}
        >
            {(isDisabled) => (
                <SelectField
                    disabled={isDisabled}
                    addEmptyOption={!isCreateForm}
                    loader={isFetching}
                    keys={commonBusinessSegmentKeys}
                />
            )}
        </LockableField>
    ) : (
        <SelectField
            disabledReasonTextKey={!isFetching ? disabledReasonTextKey : undefined}
            loader={isFetching}
            keys={commonBusinessSegmentKeys}
            disabled
        />
    );
};
